import { kidStyle } from "../kid-style";
import { signed } from "../format";
import { Emoji } from "./Emoji";
import { KidAvatar } from "./KidAvatar";
import type { KidSummary } from "../../shared/types";

type Week = KidSummary["week"];

const same = (a: Week, b: Week) => a.stars === b.stars && a.net === b.net;

/**
 * Each kid's week so far: star days, then how far the score moved since Monday. Ordered by stars
 * then net, but with no places or medals: it is two siblings, so it reads as "This week", and a
 * kid level with another is marked "level" rather than put second.
 */
export function WeekStrip({ kids }: { kids: readonly KidSummary[] }) {
  // Copied before sorting; the snapshot's own order is creation order and Home relies on it.
  const rows = [...kids].sort((a, b) => b.week.stars - a.week.stars || b.week.net - a.week.net);
  // Level with someone else in both numbers: the order between them means nothing.
  const level = (kid: KidSummary) => rows.some((other) => other.id !== kid.id && same(other.week, kid.week));

  return (
    <section className="section week" aria-labelledby="week-title">
      <h2 className="section__title" id="week-title">
        <Emoji>📅</Emoji>This week
      </h2>
      <ul className="week__list">
        {rows.map((kid) => (
          <li key={kid.id} className="week__row" style={kidStyle(kid.colour)}>
            <KidAvatar kid={kid} size="sm" />
            <span className="week__name">{kid.name}</span>
            {rows.length > 1 && level(kid) && <span className="week__level">level</span>}
            <span className="week__stars">
              <Emoji>⭐</Emoji>
              <span aria-hidden="true">{kid.week.stars}</span>
              <span className="visually-hidden">{`${kid.week.stars} star ${kid.week.stars === 1 ? "day" : "days"},`}</span>
            </span>
            <span className="week__net">
              {kid.week.net !== 0 && <Emoji>{kid.week.net > 0 ? "📈" : "📉"}</Emoji>}
              <span aria-hidden="true">{signed(kid.week.net)}</span>
              <span className="visually-hidden">{`net ${signed(kid.week.net)} points`}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="hint">Star days and points since Monday.</p>
    </section>
  );
}
